'use client'

import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { isHtmlString } from '@/lib/html'
import { cn } from '@/lib/styles'

interface Props {
  readonly children: ReactElement
  readonly content: ReactNode | string
  readonly contentProps?: Partial<ComponentPropsWithoutRef<typeof TooltipContent>>
  readonly open?: boolean
  readonly onOpenChange?: (open: boolean) => void
  readonly asChild?: boolean
}

export function Tooltip({
  children,
  content,
  contentProps,
  open: controlledOpen,
  onOpenChange,
  asChild = false,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : isOpen

  const handleOpenChange = (value: boolean) => {
    if (!isControlled) {
      setIsOpen(value)
    }
    onOpenChange?.(value)
  }

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    handleOpenChange(true)
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      handleOpenChange(false)
      closeTimeoutRef.current = null
    }, 100)
  }

  useEffect(
    () => () => {
      if (closeTimeoutRef.current !== null) {
        clearTimeout(closeTimeoutRef.current)
      }
    },
    [],
  )

  const { className, onMouseEnter, onMouseLeave, ...restContentProps } = contentProps ?? {}

  return (
    <TooltipProvider delayDuration={0}>
      <RadixTooltip open={open}>
        <TooltipTrigger
          asChild={asChild}
          {...(!asChild && { type: 'button' })}
          onClick={() => handleOpenChange(!open)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onTouchStart={() => handleOpenChange(!open)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleOpenChange(!open)
            }
          }}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          {...restContentProps}
          className={cn('max-w-50', !content && 'hidden', className)}
          onMouseEnter={(event) => {
            handleMouseEnter()
            onMouseEnter?.(event)
          }}
          onMouseLeave={(event) => {
            handleMouseLeave()
            onMouseLeave?.(event)
          }}
        >
          {typeof content === 'string' && isHtmlString(content) ? (
            <CkEditorCSRRenderer variant="textOnly" htmlContent={content} />
          ) : (
            <span className="inline-block">{content}</span>
          )}
        </TooltipContent>
      </RadixTooltip>
    </TooltipProvider>
  )
}
